import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/AdminLayout';
import adminApi from '@/lib/adminApi';
import { formatApiError } from '@/lib/AuthContext';
import { Building2, Search, Plus, ChevronLeft, ChevronRight } from 'lucide-react';

export default function AdminOrganisationsPage() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', slug: '', max_users: 10 });
  const [createError, setCreateError] = useState('');

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getOrganisations({ page, search, status: statusFilter });
      setOrgs(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) { console.error('Failed to fetch organisations:', err); }
    setLoading(false);
  }, [page, search, statusFilter]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    try {
      const { data } = await adminApi.createOrganisation(createForm);
      setShowCreate(false);
      setCreateForm({ name: '', slug: '', tier: 'free', max_users: 10 });
      fetchOrgs();
    } catch (err) {
      setCreateError(formatApiError(err.response?.data?.detail));
    }
  };

  const statusBadge = (status) => {
    const colors = { active: 'bg-green-100 text-green-700', suspended: 'bg-red-100 text-red-700' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>;
  };

  return (
    <AdminLayout title="Organisations" breadcrumbs={[{ label: 'Organisations' }]}>
      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4" data-testid="admin-orgs-toolbar">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B0BEC5]" />
          <input data-testid="admin-orgs-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 border border-[#E0E0E0] rounded-lg text-sm focus:border-[#0097A7] outline-none" placeholder="Search by name or slug..." />
        </div>
        <select data-testid="admin-orgs-status-filter" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm text-[#4E6974]">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <button data-testid="admin-orgs-create-btn" onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#00707C] text-white text-sm rounded-lg hover:bg-[#005F6A] transition-colors">
          <Plus className="h-4 w-4" /> New organisation
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#E8ECF0] overflow-hidden" data-testid="admin-orgs-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E8ECF0] bg-[#F8FAFB]">
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Name</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Slug</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Users</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="text-center py-8 text-[#B0BEC5]">Loading...</td></tr>}
            {!loading && orgs.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#B0BEC5]">No organisations found</td></tr>}
            {orgs.map(org => (
              <tr key={org.id} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                onClick={() => navigate(`/admin/organisations/${org.id}`)} data-testid={`admin-org-row-${org.slug}`}>
                <td className="px-4 py-3 font-medium text-[#0A2533]">{org.name}</td>
                <td className="px-4 py-3 text-[#4E6974] font-mono text-xs">{org.slug}</td>
                <td className="px-4 py-3 text-[#4E6974]">{org.user_count}</td>
                <td className="px-4 py-3">{statusBadge(org.status)}</td>
                <td className="px-4 py-3 text-[#B0BEC5] text-xs">{new Date(org.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8ECF0]">
            <span className="text-xs text-[#4E6974]">{total} organisations</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1 rounded hover:bg-[#F5F5F5] disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
              <span className="text-xs text-[#4E6974]">{page} / {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-1 rounded hover:bg-[#F5F5F5] disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" data-testid="admin-create-org-modal">
            <h2 className="text-lg font-bold text-[#0A2533] mb-4">Create organisation</h2>
            {createError && <div className="text-sm text-red-600 bg-red-50 rounded-lg p-2 mb-3">{createError}</div>}
            <form onSubmit={handleCreate} className="space-y-3">
              <input data-testid="admin-create-org-name" value={createForm.name} onChange={e => setCreateForm(f => ({...f, name: e.target.value}))}
                className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm focus:border-[#0097A7] outline-none" placeholder="Organisation name" required />
              <input data-testid="admin-create-org-slug" value={createForm.slug} onChange={e => setCreateForm(f => ({...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')}))}
                className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm font-mono focus:border-[#0097A7] outline-none" placeholder="organisation-slug" required />
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 py-2 border border-[#E0E0E0] rounded-lg text-sm text-[#4E6974] hover:bg-[#F5F5F5]">Cancel</button>
                <button type="submit" data-testid="admin-create-org-submit" className="flex-1 py-2 bg-[#00707C] text-white rounded-lg text-sm hover:bg-[#005F6A]">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
